import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import Downshift from 'downshift';
import intlShape from '@/shared/utils/intlShape';

import { injectStateService, StateService } from '@/shared/services/StateService';
import RelationName, {
  getDuplicateRelationNameIds,
  getRelationCode,
  getRelationName,
  getRelationNameCode,
} from '@/shared/components/RelationName';
import SearchRelation from '@/shared/components/SearchRelation';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import Item from '@/shared/components/input/select/Item';

import messages from '../dashboardMessages';

const itemToString = i => (i ? `${i.name} ${i.firstName}` : '');

const DropDownMenu = ({
  intl,
  loading,
  relations,
  error,
  highlightedIndex,
  selectedItem,
  getItemProps,
  getMenuProps,
}) => {
  let innerComponent = null;
  if (loading) innerComponent = <LoadingAnimation />;
  if (error) innerComponent = <FormattedMessage {...messages.ErrorLoadingData} />;
  if (!innerComponent && (!relations || !relations.length)) return null;
  const duplicateNameIds = innerComponent
    ? null
    : getDuplicateRelationNameIds(relations, r => getRelationName(intl, r));
  return (
    <div className="Select--relative open width100pct">
      <ul
        {...getMenuProps({
          className: 'Select--dropdown dropdown-menu width100pct dropdown-menu-right width100pct',
        })}
      >
        {innerComponent ||
          relations.map((relation, index) => {
            const code = getRelationCode(relation);
            return (
              <Item
                key={relation.id}
                item={relation}
                index={index}
                isSelected={highlightedIndex === index}
                isActive={selectedItem === relation}
                getItemProps={getItemProps}
              >
                <span title={code ? getRelationNameCode(intl, relation) : undefined}>
                  <RelationName relation={relation} enabled={false} />
                  {code && duplicateNameIds.has(relation.id) && (
                    <span className="text-muted"> | {code}</span>
                  )}
                </span>
              </Item>
            );
          })}
      </ul>
    </div>
  );
};
DropDownMenu.propTypes = {
  intl: intlShape.isRequired,
  loading: PropTypes.bool,
  relations: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })),
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.instanceOf(Error)]),
  highlightedIndex: PropTypes.number,
  selectedItem: PropTypes.shape({ id: PropTypes.string }),
  getItemProps: PropTypes.func.isRequired,
  getMenuProps: PropTypes.func.isRequired,
};
DropDownMenu.defaultProps = {
  loading: false,
  relations: null,
  error: null,
  highlightedIndex: null,
  selectedItem: null,
};

const SearchRelationDashboard = ({ intl, stateService }) => {
  const [inputValue, setInputValue] = useState('');

  const handleStateChange = useCallback(
    (changes, { clearSelection }) => {
      if (typeof changes.selectedItem !== 'undefined') {
        if (changes.selectedItem && changes.selectedItem.id) {
          stateService.gotoRelation(changes.selectedItem.id);
        }
        clearSelection();
      } else if (typeof changes.inputValue !== 'undefined') {
        setInputValue(changes.inputValue);
      }
    },
    [stateService],
  );

  return (
    <div className="search-block">
      <div className="search-box">
        <div className="content">
          <Downshift itemToString={itemToString} onStateChange={handleStateChange}>
            {({
              getInputProps,
              getMenuProps,
              isOpen,
              clearItems,
              setItemCount,
              highlightedIndex,
              getItemProps,
              selectedItem,
            }) => (
              <div>
                <label className="sr-only" htmlFor="dashboard-search-relation">
                  {intl.formatMessage(messages.SearchRelation)}
                </label>
                <input
                  {...getInputProps({
                    placeholder: intl.formatMessage(messages.SearchRelation),
                    className: 'form-control',
                    name: 'search',
                    autoComplete: 'off',
                    maxLength: '128',
                    id: 'dashboard-search-relation',
                    type: 'search',
                    onKeyDown(e) {
                      if (e.key === 'Enter' && highlightedIndex === null && e.target.value) {
                        stateService.gotoState('relations.overview', {
                          relationName: e.target.value,
                        });
                        return false;
                      }
                    },
                  })}
                />
                {!isOpen ? null : (
                  <SearchRelation
                    searchValue={inputValue}
                    onLoaded={({ relations }) => {
                      clearItems();
                      if (relations) {
                        setItemCount(relations.length);
                      }
                    }}
                  >
                    {({ loading, relations, error }) => (
                      <DropDownMenu
                        {...{
                          intl,
                          loading,
                          relations,
                          error,
                          highlightedIndex,
                          selectedItem,
                          getItemProps,
                          getMenuProps,
                        }}
                      />
                    )}
                  </SearchRelation>
                )}
              </div>
            )}
          </Downshift>
        </div>
      </div>
    </div>
  );
};
SearchRelationDashboard.propTypes = {
  intl: intlShape.isRequired,
  stateService: PropTypes.instanceOf(StateService).isRequired,
};

export default injectStateService(injectIntl(SearchRelationDashboard));
